/**
 * Money helpers.
 *
 * Every rupee figure in this system is a Decimal or a fixed-2dp string — never a
 * JavaScript number in intermediate arithmetic. `0.1 + 0.2` is famously not `0.3`,
 * and on an invoice that discrepancy eventually becomes a rupee the customer
 * queries and a total that doesn't reconcile.
 *
 * The counter enters rupees and paisa in two separate boxes (as specified); those
 * collapse into one DECIMAL(14,2) column via `fromRupeesPaisa`.
 */
import Decimal from 'decimal.js';

// 2dp half-up matches how a bill is read aloud and how GST computations are expected
// to round. Set once, globally, so no call site can quietly differ.
Decimal.set({ precision: 28, rounding: Decimal.ROUND_HALF_UP });

/** Coerce anything (string | number | Decimal | null) into a Decimal, defaulting to 0. */
export function money(value) {
  if (value === null || value === undefined || value === '') return new Decimal(0);
  if (value instanceof Decimal) return value;
  const d = new Decimal(String(value).replace(/,/g, '').trim());
  if (d.isNaN()) throw new TypeError(`Not a valid amount: ${value}`);
  return d;
}

/** Round to paise. Every value that lands in the database passes through here. */
export function round2(value) {
  return money(value).toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
}

/** Serialise for a DECIMAL(14,2) column or a JSON payload. Always "1234.50", never 1234.5. */
export function toAmount(value) {
  return round2(value).toFixed(2);
}

/** Two counter inputs -> one stored amount. */
export function fromRupeesPaisa(rupees, paisa) {
  const p = Math.trunc(Number(paisa) || 0);
  if (p < 0 || p > 99) throw new RangeError(`Paisa must be 0-99, got ${paisa}`);
  return round2(money(rupees).plus(new Decimal(p).dividedBy(100)));
}

/** One stored amount -> two counter inputs. */
export function toRupeesPaisa(value) {
  const v = round2(value);
  const rupees = v.toDecimalPlaces(0, Decimal.ROUND_DOWN);
  return { rupees: rupees.toNumber(), paisa: v.minus(rupees).times(100).round().toNumber() };
}

/**
 * Round a bill total to the nearest rupee and report the adjustment separately,
 * so the printed invoice always reconciles: taxable + tax + roundOff = grandTotal.
 */
export function roundOff(value) {
  const exact = round2(value);
  const rounded = exact.toDecimalPlaces(0, Decimal.ROUND_HALF_UP);
  return { grandTotal: rounded, roundOff: round2(rounded.minus(exact)) };
}

export function sum(values) {
  return values.reduce((acc, v) => acc.plus(money(v)), new Decimal(0));
}

/** Indian digit grouping: 12,34,567.89 — not the 1,234,567.89 that Intl gives by default. */
export function formatINR(value, { symbol = true, decimals = 2 } = {}) {
  const v = round2(value);
  const negative = v.isNegative();
  const [whole, fraction = ''] = v.abs().toFixed(decimals).split('.');

  // Last three digits, then pairs: 1234567 -> 12,34,567
  let grouped;
  if (whole.length <= 3) {
    grouped = whole;
  } else {
    const head = whole.slice(0, -3);
    const tail = whole.slice(-3);
    grouped = `${head.replace(/\B(?=(\d{2})+(?!\d))/g, ',')},${tail}`;
  }

  const body = decimals > 0 ? `${grouped}.${fraction}` : grouped;
  return `${negative ? '-' : ''}${symbol ? '₹' : ''}${body}`;
}

export { Decimal };
